/**
 * The Tasks-board rollup key — ONE copy, imported by the client board
 * (`client/src/components/tasks/helpers.ts` → `rollupKeyForTask`) and the
 * server badge (`server/src/routes/tasks.ts` → `GET /api/tasks/counts`).
 *
 * The sidebar badge used to count raw BlockingTask rows (one per SHIPMENT)
 * while the page listed one ticket per rollup key, so a school with four
 * Aug 13 shipments saw four badge units for one Overdue row (Amana,
 * 2026-11-04: badge 36, page ~14). Both sides now key off this function, so
 * the badge counts exactly the rows the page renders.
 *
 * Grouping axes:
 *   - CHEF_SUBMIT_TEMP_LOG → one per DELIVERY day, all campuses inside.
 *   - SCHOOL_COMPLETE_SERVICE / SCHOOL_CONFIRM_DELIVERY_RECEIVED → one per
 *     (campus, SERVICE day) — the unit the Production Records page works in.
 *
 * Null (no grouping) for other subKinds, rows without shipment metadata and
 * closed rows. Pure — no imports, so it stays safe for both bundles.
 */

export interface TaskRollupKeyInput {
  subKind: string | null
  status: string
  /** ISO yyyy-mm-dd calendar days, as the list projection emits them. */
  shipment?: { schoolLocationId: string; serviceDate: string; deliveryDate: string } | null
}

/** SubKinds whose board ticket is one (campus, service day). */
export const SCHOOL_DAY_ROLLUP_SUB_KINDS: ReadonlySet<string> = new Set([
  'SCHOOL_COMPLETE_SERVICE',
  'SCHOOL_CONFIRM_DELIVERY_RECEIVED',
])

export function taskRollupKey(task: TaskRollupKeyInput): string | null {
  const s = task.shipment
  if (!s || (task.status !== 'OPEN' && task.status !== 'IN_PROGRESS')) {
    return null
  }
  if (task.subKind === 'CHEF_SUBMIT_TEMP_LOG') {
    return `CHEF_SUBMIT_TEMP_LOG|${s.deliveryDate}`
  }
  if (task.subKind !== null && SCHOOL_DAY_ROLLUP_SUB_KINDS.has(task.subKind)) {
    return `${task.subKind}|${s.schoolLocationId}|${s.serviceDate}`
  }
  return null
}
